import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import SignaturePad from 'signature_pad';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eraser, FileText, Package, AlertTriangle, Camera, X, PenLine, Share2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { maskCpfCnpj } from '@/utils/formatters';
import { resizeImageToJpeg } from '@/utils/resizeImage';
import { buildSignaturePath } from '@/lib/signatureStorage';
import { friendlyContractError, buildContractPdfProps } from '@/lib/rentalContract';
import { fetchRentalCollectionItems } from '@/lib/rentalCollectionItems';
import { buildSignedContractPdf } from '@/lib/signedContractPdf';
import { generateContractPDF } from '@/utils/pdfGenerator';
import { generatePdfFileName } from '@/utils/fileNameUtils';
import { deliverPdf } from '@/utils/deliverPdf';
import { ContractTemplate } from '@/components/Documents/Templates/ContractTemplate';
import type { CollectionTermItem } from '@/components/Documents/Templates/CollectionTermDocument';

// L-ASS.3 FRONT-J2 (K2): assinatura presencial da ENTREGA DE LOCAÇÃO (origin_type = 'rental').
// Contrato 'signed' => só termo de entrega. 'pending'/'expired' com content_hash => quem recebe lê o texto
// congelado e aceita (D-ASS.23). Senão => indisponível. Foto do documento obrigatória; falha no upload
// dela bloqueia (D-ASS.20). RPC: confirm_rental_delivery_with_signature.

interface RentalDeliverySignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryId: string;
  deliveryNumber: string;
  onSuccess?: () => void;
}

interface ContractRow {
  id: string;
  status: string;
  contract_title: string | null;
  contract_content: string | null;
  contract_summary: Record<string, unknown> | null;
  content_hash: string | null;
  customer_email: string | null;
}

type ContractState = 'signed' | 'toSign' | 'unavailable';

// E2: após a RPC, o diálogo fica aberto no estado 'done' com Compartilhar / Fechar
interface DoneState {
  signatureId: string | null;
  signedNow: boolean;
}

// Converter base64 para blob (cópia local — o helper do DeliverySignatureDialog não é exportado)
const dataURLtoBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

export const RentalDeliverySignatureDialog = ({
  open,
  onOpenChange,
  deliveryId,
  deliveryNumber,
  onSuccess,
}: RentalDeliverySignatureDialogProps) => {
  const { company } = useAuth();
  const queryClient = useQueryClient();

  // Carga
  const [loadingData, setLoadingData] = useState(false);
  const [rentalId, setRentalId] = useState<string | null>(null);
  const [rentalNumber, setRentalNumber] = useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [contract, setContract] = useState<ContractRow | null>(null);
  const [items, setItems] = useState<CollectionTermItem[]>([]);

  // Formulário
  const [customerName, setCustomerName] = useState('');
  const [customerDocument, setCustomerDocument] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [documentPhotoFile, setDocumentPhotoFile] = useState<File | null>(null);
  const [documentPhotoPreview, setDocumentPhotoPreview] = useState<string | null>(null);
  const documentPhotoRef = useRef<HTMLInputElement>(null);

  // Envio
  const [submitting, setSubmitting] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string | null>(null);
  const [done, setDone] = useState<DoneState | null>(null);
  const [sharing, setSharing] = useState(false);

  // Assinatura (molde PickupSignatureDialog)
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [canvasReady, setCanvasReady] = useState(false);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !signaturePadRef.current) return;

    const savedData = signaturePadRef.current.isEmpty()
      ? null
      : signaturePadRef.current.toData();

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;

    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    signaturePadRef.current.clear();
    if (savedData) signaturePadRef.current.fromData(savedData);
  }, []);

  const setCanvasCallback = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      canvasRef.current = canvas;
      if (canvas && open) setCanvasReady(true);
    },
    [open]
  );

  // Carga ao abrir (com company_id do usuário)
  useEffect(() => {
    if (!open || !deliveryId || !company?.id) return;

    let cancelled = false;
    (async () => {
      setLoadingData(true);
      try {
        const { data: deliveryRow, error: deliveryError } = await supabase
          .from('deliveries')
          .select('rental_id, contact_name')
          .eq('id', deliveryId)
          .eq('company_id', company.id)
          .maybeSingle();
        if (deliveryError) throw deliveryError;

        const rid: string | null = (deliveryRow as any)?.rental_id ?? null;
        if (cancelled) return;
        setRentalId(rid);
        setContactName((deliveryRow as any)?.contact_name ?? '');

        if (!rid) {
          setContract(null);
          setItems([]);
          return;
        }

        const [{ data: rentalRow }, { data: sigRow }, collectionItems] = await Promise.all([
          supabase
            .from('rentals')
            .select('rental_number')
            .eq('id', rid)
            .eq('company_id', company.id)
            .maybeSingle(),
          supabase
            .from('contract_signatures')
            .select('id, status, contract_title, contract_content, contract_summary, content_hash, customer_email')
            .eq('contract_id', rid)
            .eq('company_id', company.id)
            .eq('signature_type', 'contract')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          fetchRentalCollectionItems(rid, company.id, undefined),
        ]);
        if (cancelled) return;

        setRentalNumber((rentalRow as any)?.rental_number ?? '');
        // types.ts defasado para as colunas congeladas (L-ASS.1 SQL-G) — cast documentado
        setContract((sigRow as unknown as ContractRow | null) ?? null);
        setItems(collectionItems);
      } catch (error) {
        console.error('Erro ao carregar dados da entrega de locação:', error);
        if (!cancelled) toast.error('Erro ao carregar os dados da entrega.');
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, deliveryId, company?.id]);

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      setCanvasReady(false);
      setCustomerName('');
      setCustomerDocument('');
      setAccepted(false);
      setDocumentPhotoFile(null);
      setDocumentPhotoPreview(null);
      setLoadingStep(null);
      setDone(null);
      setSharing(false);
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    }
  }, [open]);

  // Listener de resize
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [open, resizeCanvas]);

  // Inicializar SignaturePad quando canvas montar
  useEffect(() => {
    if (!canvasReady || !canvasRef.current || !open) return;

    const canvas = canvasRef.current;
    const timer = setTimeout(() => {
      try {
        if (signaturePadRef.current) signaturePadRef.current.off();

        signaturePadRef.current = new SignaturePad(canvas, {
          backgroundColor: 'rgb(255, 255, 255)',
          penColor: 'rgb(0, 0, 0)',
          velocityFilterWeight: 0.7,
          minWidth: 0.5,
          maxWidth: 2.5,
        });

        resizeCanvas();
      } catch (err) {
        console.error('Erro ao inicializar SignaturePad:', err);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [canvasReady, open, resizeCanvas]);

  const handleClear = () => {
    signaturePadRef.current?.clear();
  };

  const handleDocumentPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (documentPhotoPreview) URL.revokeObjectURL(documentPhotoPreview);
    setDocumentPhotoFile(file);
    setDocumentPhotoPreview(URL.createObjectURL(file));
  };

  const handleRemoveDocumentPhoto = () => {
    if (documentPhotoPreview) URL.revokeObjectURL(documentPhotoPreview);
    setDocumentPhotoFile(null);
    setDocumentPhotoPreview(null);
    if (documentPhotoRef.current) documentPhotoRef.current.value = '';
  };

  // M3: compartilhar o contrato ANTES de assinar — PDF do texto congelado, sem assinatura
  const handleShareFrozenContract = async () => {
    if (!contract?.contract_content || sharing) return;
    setSharing(true);
    try {
      const blob = await generateContractPDF(
        buildContractPdfProps(contract.contract_summary ?? {}, {
          title: contract.contract_title ?? '',
          content: contract.contract_content,
        })
      );
      await deliverPdf(blob, generatePdfFileName(contactName || customerName, 'Contrato', rentalNumber));
    } catch (err: any) {
      console.error('Erro ao gerar PDF do contrato:', err);
      toast.error('Erro ao gerar PDF do contrato');
    } finally {
      setSharing(false);
    }
  };

  // E2: compartilhar o contrato ASSINADO após a RPC — gerador único (lib), com evidências presenciais
  const handleShareSignedContract = async () => {
    if (!done?.signatureId || !company?.id || sharing) return;
    setSharing(true);
    try {
      const result = await buildSignedContractPdf({
        signatureId: done.signatureId,
        companyId: company.id,
        rentalNumber,
      });
      if (!result) {
        toast.error('Contrato assinado não encontrado.');
        return;
      }
      await deliverPdf(result.blob, result.fileName);
    } catch (err: any) {
      console.error('Erro ao gerar PDF do contrato assinado:', err);
      toast.error(friendlyContractError(err?.message ?? 'Erro ao gerar PDF do contrato assinado'));
    } finally {
      setSharing(false);
    }
  };

  const handleClose = () => {
    onSuccess?.();
    onOpenChange(false);
  };

  // Estado do contrato (mesma regra da RPC)
  const contractState: ContractState = (() => {
    if (!contract) return 'unavailable';
    if (contract.status === 'signed') return 'signed';
    if ((contract.status === 'pending' || contract.status === 'expired') && contract.content_hash) return 'toSign';
    return 'unavailable';
  })();

  const handleConfirm = async () => {
    if (submitting) return;
    if (!company?.id) {
      toast.error('Empresa não identificada. Entre novamente.');
      return;
    }
    if (contractState === 'unavailable') {
      toast.error('Contrato alterado ou não enviado. Peça ao escritório para enviar para entrega de novo.');
      return;
    }
    if (!customerName.trim()) {
      toast.error('Informe o nome de quem recebe.');
      return;
    }
    const documentDigits = customerDocument.replace(/\D/g, '');
    if (documentDigits.length !== 11 && documentDigits.length !== 14) {
      toast.error('Documento inválido — informe um CPF (11 dígitos) ou CNPJ (14 dígitos).');
      return;
    }
    if (!documentPhotoFile) {
      toast.error('A foto do documento de quem recebe é obrigatória.');
      return;
    }
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      toast.error('Colha a assinatura de quem recebe.');
      return;
    }
    if (contractState === 'toSign' && !accepted) {
      toast.error('Confirme que quem recebe leu e aceita o contrato.');
      return;
    }

    setSubmitting(true);
    try {
      // 2) Foto do documento — obrigatória; falha aqui BLOQUEIA (D-ASS.20)
      setLoadingStep('Enviando foto do documento...');
      let documentPhotoUrl: string;
      try {
        const jpeg = await resizeImageToJpeg(documentPhotoFile);
        const photoPath = buildSignaturePath(company.id, deliveryId, `document_${Date.now()}.jpg`);
        const { data: photoUpload, error: photoError } = await supabase.storage
          .from('signatures')
          .upload(photoPath, jpeg, { contentType: 'image/jpeg', upsert: false });
        if (photoError || !photoUpload) throw photoError ?? new Error('Falha no upload da foto');
        documentPhotoUrl = supabase.storage.from('signatures').getPublicUrl(photoUpload.path).data.publicUrl;
      } catch (photoErr: any) {
        console.error('Erro ao enviar a foto do documento:', photoErr);
        toast.error('Não foi possível enviar a foto do documento. Tente novamente.');
        return;
      }

      // 3) Assinatura — mesmo molde do DeliverySignatureDialog; falha aqui NÃO bloqueia
      setLoadingStep('Salvando assinatura...');
      const signatureDataUrl = signaturePadRef.current.toDataURL();
      let signatureUrl: string | null = null;
      try {
        const blob = dataURLtoBlob(signatureDataUrl);
        const fileName = buildSignaturePath(company.id, deliveryId, `${Date.now()}.png`);
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('signatures')
          .upload(fileName, blob, { contentType: 'image/png', upsert: false });
        if (uploadError) {
          console.warn('Erro ao fazer upload da assinatura:', uploadError);
        } else {
          signatureUrl = supabase.storage.from('signatures').getPublicUrl(uploadData.path).data.publicUrl;
        }
      } catch (uploadErr) {
        console.warn('Erro no upload da assinatura:', uploadErr);
      }

      // 4) Confirmação atômica no banco
      setLoadingStep('Confirmando entrega...');
      const { data, error } = await (supabase.rpc as any)('confirm_rental_delivery_with_signature', {
        p_delivery_id: deliveryId,
        p_signature_data: signatureDataUrl,
        p_signature_url: signatureUrl,
        p_customer_name: customerName.trim(),
        p_customer_document: documentDigits,
        p_signer_document_photo_url: documentPhotoUrl,
        p_accepted: contractState === 'toSign' ? true : null,
      });
      if (error) throw error;

      // 5) Sucesso — E2: o diálogo fica aberto no estado 'done' (Compartilhar contrato assinado / Fechar);
      // onSuccess e fechamento do pai só no "Fechar". Invalidações seguem aqui.
      const signedNow = data?.contract_signed_now === true;
      toast.success(signedNow ? 'Entrega confirmada e contrato assinado' : 'Entrega confirmada');
      queryClient.invalidateQueries({ queryKey: ['driver-route-deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['rental-delivery'] });
      queryClient.invalidateQueries({ queryKey: ['rental-contract-signature'] });
      if (rentalId) queryClient.invalidateQueries({ queryKey: ['rental-receipt-history', rentalId] });
      setDone({ signatureId: (data?.signature_id as string | undefined) ?? contract?.id ?? null, signedNow });
    } catch (err: any) {
      console.error('Erro ao confirmar entrega de locação:', err);
      toast.error(friendlyContractError(err?.message ?? 'Erro ao confirmar a entrega'));
    } finally {
      setLoadingStep(null);
      setSubmitting(false);
    }
  };

  const canConfirm =
    !loadingData &&
    !submitting &&
    contractState !== 'unavailable' &&
    !!customerName.trim() &&
    !!documentPhotoFile &&
    (contractState !== 'toSign' || accepted);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (submitting) return;
        // R1: no estado 'done', fechar por fora (X/overlay) equivale ao "Fechar"; com compartilhamento em curso, ignora
        if (done && !nextOpen) {
          if (sharing) return;
          handleClose();
          return;
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="flex-shrink-0 px-6 pt-6">
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5" />
            Entrega de Locação - {deliveryNumber}
          </DialogTitle>
          <DialogDescription>
            {rentalNumber ? `Contrato ${rentalNumber}` : 'Contrato da locação'}
            {contactName ? ` • ${contactName}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {done ? (
            /* E2: tela final — entrega confirmada; compartilhar o contrato assinado ou fechar */
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CheckCircle className="h-12 w-12 text-success" />
              <p className="font-medium">
                {done.signedNow ? 'Entrega confirmada e contrato assinado' : 'Entrega confirmada'}
              </p>
              <p className="text-sm text-muted-foreground">
                {done.signedNow
                  ? 'O contrato foi assinado presencialmente por quem recebeu o equipamento.'
                  : 'O contrato já estava assinado; esta assinatura registra o recebimento do equipamento.'}
              </p>
            </div>
          ) : loadingData ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando dados da entrega...
            </div>
          ) : (
            <>
              {/* Estado do contrato */}
              {contractState === 'unavailable' && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Contrato alterado ou não enviado. Peça ao escritório para enviar para entrega de novo.
                  </AlertDescription>
                </Alert>
              )}
              {contractState === 'signed' && (
                <Alert>
                  <FileText className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    Contrato já assinado. Esta assinatura registra o recebimento do equipamento.
                  </AlertDescription>
                </Alert>
              )}
              {contractState === 'toSign' && contract && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5" />
                      Contrato para aceite
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      onClick={handleShareFrozenContract}
                      disabled={sharing || submitting}
                    >
                      {sharing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Share2 className="h-3 w-3" />}
                      Compartilhar contrato
                    </Button>
                  </div>
                  {/* M4 (m-ASS.17): mesmo molde da página pública — texto congelado formatado pelo ContractTemplate */}
                  <div className="border rounded-lg max-h-[40vh] overflow-y-auto bg-muted/30">
                    <ContractTemplate
                      data={(contract.contract_summary ?? {}) as unknown as Parameters<typeof ContractTemplate>[0]['data']}
                      title={contract.contract_title ?? undefined}
                      content={contract.contract_content ?? undefined}
                    />
                  </div>
                  {contract.content_hash && (
                    <p className="text-xs text-muted-foreground">
                      Código de verificação: {contract.content_hash.slice(0, 16)}
                    </p>
                  )}
                  <div className="flex items-start gap-3 rounded-md border p-3">
                    <Checkbox
                      id="rental-delivery-accept"
                      checked={accepted}
                      onCheckedChange={(checked) => setAccepted(checked === true)}
                      disabled={submitting}
                      className="mt-0.5"
                    />
                    <Label htmlFor="rental-delivery-accept" className="text-sm leading-snug cursor-pointer">
                      Quem recebe leu e aceita o contrato
                    </Label>
                  </div>
                </div>
              )}

              {/* Itens entregues (read-only) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5" />
                  Itens entregues
                </Label>
                <div className="bg-muted/50 rounded-md p-3 space-y-1.5 text-sm">
                  {items.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Nenhum item vinculado ao contrato foi encontrado.
                    </p>
                  ) : (
                    items.map((ci, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-sm leading-tight line-clamp-2">{ci.name}</p>
                          {ci.serial_number && (
                            <p className="text-xs text-muted-foreground">S/N {ci.serial_number}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {ci.quantity}x
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Quem recebe */}
              <div className="space-y-1.5">
                <Label htmlFor="rental-delivery-name">
                  Nome de quem recebe <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="rental-delivery-name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Nome completo"
                  disabled={submitting}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="rental-delivery-document">
                  CPF/CNPJ <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="rental-delivery-document"
                  value={customerDocument}
                  onChange={(e) => setCustomerDocument(maskCpfCnpj(e.target.value))}
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  maxLength={18}
                  disabled={submitting}
                />
              </div>

              {/* Foto do documento */}
              <div className="space-y-1.5">
                <Label>
                  Foto do documento <span className="text-destructive">*</span>
                </Label>
                <input
                  ref={documentPhotoRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleDocumentPhotoSelect}
                  disabled={submitting}
                  className="hidden"
                  id="rental-delivery-document-photo"
                />
                {!documentPhotoPreview ? (
                  <label
                    htmlFor="rental-delivery-document-photo"
                    className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center cursor-pointer flex flex-col items-center gap-1.5 hover:border-muted-foreground/50 transition-colors"
                  >
                    <Camera className="h-6 w-6 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Tirar foto ou escolher imagem do documento</span>
                  </label>
                ) : (
                  <div className="border rounded-lg p-2 flex items-center gap-3 bg-muted/50">
                    <img
                      src={documentPhotoPreview}
                      alt="Documento de quem recebe"
                      className="w-20 h-14 object-cover rounded border border-border"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{documentPhotoFile?.name}</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => documentPhotoRef.current?.click()}
                      disabled={submitting}
                    >
                      Trocar
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveDocumentPhoto}
                      disabled={submitting}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Canvas de assinatura */}
              <div className="space-y-1.5">
                <Label>
                  Assinatura de quem recebe <span className="text-destructive">*</span>
                </Label>
                <div className="border rounded-md overflow-hidden bg-white">
                  <canvas
                    ref={setCanvasCallback}
                    className="w-full touch-none"
                    style={{ height: 180, display: 'block' }}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={handleClear}
                  disabled={submitting}
                >
                  <Eraser className="h-3.5 w-3.5 mr-1.5" />
                  Limpar assinatura
                </Button>
              </div>
            </>
          )}
        </div>

        {loadingStep && (
          <div className="flex-shrink-0 px-6 pb-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {loadingStep}
            </div>
          </div>
        )}

        <DialogFooter className="flex-shrink-0 border-t px-6 py-4">
          {done ? (
            <>
              <Button
                variant="outline"
                onClick={handleShareSignedContract}
                disabled={sharing || !done.signatureId}
              >
                {sharing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Share2 className="h-4 w-4 mr-2" />
                )}
                Compartilhar contrato assinado
              </Button>
              <Button onClick={handleClose} disabled={sharing}>
                Fechar
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancelar
              </Button>
              <Button onClick={handleConfirm} disabled={!canConfirm}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Confirmando...
                  </>
                ) : (
                  <>
                    <PenLine className="h-4 w-4 mr-2" />
                    Confirmar Entrega
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
